import { useState, useRef, useEffect } from 'react';
import type { Comment } from '../api/client';
import { Avatar } from './AppShell';
import { relativeTime } from '../utils';
import ReactionPicker from './comments/ReactionPicker';

interface CommentThreadProps {
  comment: Comment;
  isOwn: boolean;
  currentUserId?: string;
  resolving?: boolean;
  highlighted?: boolean;
  allowResolve?: boolean;
  allowReactions?: boolean;
  canReply?: boolean;
  onResolve: (commentId: string) => void;
  onUnresolve: (commentId: string) => void;
  onToggleReaction: (commentId: string, emoji: string) => void;
  onSubmitReply?: (parentId: string, body: string) => Promise<void>;
  onFocusMarker?: (comment: Comment) => void;
  onCommentHover?: (comment: Comment) => void;
  onCommentLeave?: (comment: Comment) => void;
}

/** Renders a comment body with @mentions highlighted. */
function CommentBody({ body, mentions }: { body: string; mentions: Comment['mentions'] }) {
  if (!mentions || mentions.length === 0) {
    return (
      <p
        className="text-[13px] leading-[1.55] m-0"
        style={{ color: 'var(--rd-text-medium)' }}
      >
        {body}
      </p>
    );
  }

  // Build a regex that matches any @Name from the mentions list
  const mentionNames = mentions.map((m) => m.name).filter(Boolean);
  if (mentionNames.length === 0) {
    return (
      <p
        className="text-[13px] leading-[1.55] m-0"
        style={{ color: 'var(--rd-text-medium)' }}
      >
        {body}
      </p>
    );
  }

  const escaped = mentionNames.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(@(?:${escaped.join('|')}))`, 'g');
  const parts = body.split(regex);

  return (
    <p
      className="text-[13px] leading-[1.55] m-0"
      style={{ color: 'var(--rd-text-medium)' }}
    >
      {parts.map((part, i) =>
        regex.test(part) ? (
          <span
            key={i}
            className="font-semibold rounded-[3px] px-[2px]"
            style={{
              color: 'var(--rd-accent)',
              background: 'var(--rd-surface-highlight)',
            }}
          >
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </p>
  );
}


function CommentBubble({
  comment,
  isOwn: _isOwn,
  isReply,
  isResolved,
  anchor,
  onReply,
  onResolve,
  onUnresolve,
  onToggleReaction,
  allowResolve = true,
  allowReactions = true,
}: {
  comment: Comment;
  isOwn: boolean;
  isReply?: boolean;
  isResolved?: boolean;
  anchor?: Comment['anchor'];
  allowResolve?: boolean;
  allowReactions?: boolean;
  onReply?: (commentId: string) => void;
  onResolve?: (commentId: string) => void;
  onUnresolve?: (commentId: string) => void;
  onToggleReaction: (commentId: string, emoji: string) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div
      className="flex gap-[10px]"
      style={{ marginLeft: isReply ? '36px' : undefined }}
    >
      {/* Avatar */}
      <div className="flex-shrink-0">
        <Avatar
          name={comment.author.name}
          avatarUrl={comment.author.avatar_url ?? undefined}
          size={26}
        />
      </div>

      {/* Body column */}
      <div className="flex-1 min-w-0">
        {/* Author line */}
        <div className="flex items-center gap-[6px]" style={{ marginBottom: '3px' }}>
          <span
            className="text-[13px] font-semibold"
            style={{ color: 'var(--rd-text-strong)' }}
          >
            {comment.author.name}
          </span>
          <span
            className="text-[13px]"
            style={{ color: 'var(--rd-text-faint)' }}
          >
            {relativeTime(comment.created_at)}
          </span>
          {isResolved && !isReply && (
            <span
              className="text-[13px] font-semibold px-[6px] py-[2px] rounded-[4px]"
              style={{ background: 'var(--rd-surface-success)', color: 'var(--rd-success-text)' }}
            >
              Resolved
            </span>
          )}
        </div>

        {/* Anchor chip — between author line and body */}
        {anchor && anchor.type !== 'page' && anchor.type !== 'point' && (
          <div
            className="inline-flex text-[13px] rounded-[5px] mb-[6px]"
            style={{
              background: 'var(--rd-surface-hover)',
              borderLeft: '2px solid var(--rd-text-faint)',
              color: 'var(--rd-avatar-default)',
              padding: '3px 10px',
              maxWidth: '100%',
            }}
          >
            {anchor.type === 'text' && (
              <span
                className="italic block max-w-full"
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                &ldquo;{anchor.text.length > 80
                  ? anchor.text.slice(0, 80).replace(/\s+\S*$/, '') + '\u2026'
                  : anchor.text}&rdquo;
              </span>
            )}
            {anchor.type === 'element' && (
              <span>{anchor.label || 'Element'}</span>
            )}
          </div>
        )}

        {/* Body with mentions */}
        <CommentBody body={comment.body} mentions={comment.mentions} />

        {/* Reactions */}
        {allowReactions && comment.reactions && comment.reactions.length > 0 && (
          <div className="flex gap-[4px] flex-wrap" style={{ marginTop: '6px' }}>
            {comment.reactions.map((r) => (
              <button
                key={r.emoji}
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleReaction(comment.id, r.emoji);
                }}
                className="inline-flex items-center gap-[2px] text-[13px] cursor-pointer transition-colors duration-[120ms]"
                style={{
                  padding: '2px 8px',
                  borderRadius: '100px',
                  background: r.reacted ? 'var(--rd-surface-highlight)' : 'var(--rd-surface-hover)',
                  border: r.reacted ? '1px solid var(--rd-border)' : '1px solid var(--rd-surface-highlight)',
                  color: 'var(--rd-text-medium)',
                }}
                title={r.users.map((u) => u.name).join(', ')}
              >
                <span>{r.emoji}</span>
                <span className="text-[13px] font-semibold">{r.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* Actions row */}
        <div className="flex items-center gap-[4px]" style={{ marginTop: '8px' }}>
          {/* Add reaction */}
          {allowReactions && (
            <div className="relative">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setPickerOpen(!pickerOpen);
                }}
                className="flex items-center justify-center w-[26px] h-[26px] rounded-[6px] cursor-pointer border-0 bg-transparent transition-colors duration-[120ms]"
                style={{ color: 'var(--rd-text-faint)' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--rd-accent)'; e.currentTarget.style.background = 'var(--rd-surface-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--rd-text-faint)'; e.currentTarget.style.background = 'transparent'; }}
                aria-label="Add reaction"
                title="Add reaction"
              >
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4" />
                  <circle cx="5.75" cy="6.5" r="0.85" fill="currentColor" />
                  <circle cx="10.25" cy="6.5" r="0.85" fill="currentColor" />
                  <path d="M5.2 9.6c.7.9 1.67 1.35 2.8 1.35 1.13 0 2.1-.45 2.8-1.35" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                </svg>
              </button>
              {pickerOpen && (
                <ReactionPicker
                  onSelect={(emoji) => {
                    onToggleReaction(comment.id, emoji);
                    setPickerOpen(false);
                  }}
                  onClose={() => setPickerOpen(false)}
                />
              )}
            </div>
          )}

          {!isReply && onReply && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onReply(comment.id);
              }}
              className="text-[13px] font-medium cursor-pointer border-0 bg-transparent rounded-[5px] transition-colors duration-[120ms]"
              style={{ color: 'var(--rd-text-faint)', padding: '3px 8px' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--rd-accent)'; e.currentTarget.style.background = 'var(--rd-surface-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--rd-text-faint)'; e.currentTarget.style.background = 'transparent'; }}
              title="Reply to this comment"
            >
              Reply
            </button>
          )}

          {/* Resolve / Unresolve for root comments only */}
          {allowResolve && !isReply && onResolve && onUnresolve && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                comment.resolved_at
                  ? onUnresolve(comment.id)
                  : onResolve(comment.id);
              }}
              className="flex items-center gap-[var(--sp-1)] text-[13px] font-medium cursor-pointer border-0 bg-transparent rounded-[5px] transition-colors duration-[120ms]"
              style={{
                color: comment.resolved_at ? 'var(--rd-success-text)' : 'var(--rd-text-faint)',
                padding: '3px 8px',
              }}
              onMouseEnter={(e) => { if (!comment.resolved_at) { e.currentTarget.style.color = 'var(--rd-accent)'; e.currentTarget.style.background = 'var(--rd-surface-hover)'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.color = comment.resolved_at ? 'var(--rd-success-text)' : 'var(--rd-text-faint)'; e.currentTarget.style.background = 'transparent'; }}
              title={comment.resolved_at ? 'Unresolve' : 'Resolve'}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.5 9.5L2.5 6.5L3.205 5.795L5.5 8.085L10.795 2.795L11.5 3.5L5.5 9.5Z"
                  fill="currentColor"
                />
                <circle
                  cx="7"
                  cy="7"
                  r="6"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  fill="none"
                />
              </svg>
              {comment.resolved_at ? 'Unresolve' : 'Resolve'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReplyReactionButton({ onToggleReaction, commentId }: { onToggleReaction: (commentId: string, emoji: string) => void; commentId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="w-[28px] h-[28px] flex items-center justify-center rounded-[7px] cursor-pointer border transition-colors duration-[120ms]"
        style={{
          background: 'transparent',
          borderColor: 'var(--rd-border)',
          color: 'var(--rd-text-faint)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--rd-surface-hover)'; e.currentTarget.style.color = 'var(--rd-accent)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--rd-text-faint)'; }}
        aria-label="Insert emoji"
        title="Insert emoji"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4" />
          <circle cx="5.75" cy="6.5" r="0.85" fill="currentColor" />
          <circle cx="10.25" cy="6.5" r="0.85" fill="currentColor" />
          <path d="M5.2 9.6c.7.9 1.67 1.35 2.8 1.35 1.13 0 2.1-.45 2.8-1.35" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <ReactionPicker
          onSelect={(emoji) => { onToggleReaction(commentId, emoji); setOpen(false); }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

export default function CommentThread({
  comment,
  isOwn,
  currentUserId,
  resolving,
  highlighted,
  onResolve,
  onUnresolve,
  onToggleReaction,
  onSubmitReply,
  allowResolve = true,
  allowReactions = true,
  canReply = true,
  onFocusMarker,
  onCommentHover,
  onCommentLeave,
}: CommentThreadProps) {
  const isResolved = !!comment.resolved_at;
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (replyOpen) {
      setTimeout(() => replyInputRef.current?.focus(), 0);
    }
  }, [replyOpen]);

  // Flash highlight when this thread is targeted by a marker click
  useEffect(() => {
    if (highlighted && threadRef.current) {
      threadRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlighted]);

  const handleReplySubmit = async () => {
    if (!replyText.trim() || replySubmitting || !onSubmitReply) return;
    setReplySubmitting(true);
    try {
      await onSubmitReply(comment.id, replyText.trim());
      setReplyText('');
      setReplyOpen(false);
    } finally {
      setReplySubmitting(false);
    }
  };

  return (
    <div
      ref={threadRef}
      className="flex flex-col"
      data-comment-id={comment.id}
      style={{
        marginBottom: '20px',
        transition: 'opacity 0.3s, transform 0.3s, max-height 0.3s, outline-color 0.3s',
        opacity: resolving ? 0 : isResolved ? 0.5 : 1,
        transform: resolving ? 'translateX(100%)' : 'none',
        maxHeight: resolving ? '0px' : 'none',
        overflow: resolving ? 'hidden' : 'visible',
        borderRadius: 'var(--radius-md)',
        outline: highlighted ? '2px solid var(--rd-text-placeholder)' : '2px solid transparent',
      }}
      onMouseEnter={() => onCommentHover?.(comment)}
      onMouseLeave={() => onCommentLeave?.(comment)}
      onClick={() => onFocusMarker?.(comment)}
    >
      <CommentBubble
        comment={comment}
        isOwn={isOwn}
        isResolved={isResolved}
        anchor={comment.anchor}
        onReply={canReply ? () => setReplyOpen(!replyOpen) : undefined}
        onResolve={onResolve}
        onUnresolve={onUnresolve}
        onToggleReaction={onToggleReaction}
        allowResolve={allowResolve}
        allowReactions={allowReactions}
      />
      {comment.replies?.map((reply) => (
        <div key={reply.id} style={{ marginTop: '12px' }}>
          <CommentBubble
            comment={reply}
            isOwn={reply.author.id === currentUserId}
            isReply
            isResolved={isResolved}
            onToggleReaction={onToggleReaction}
            allowResolve={allowResolve}
            allowReactions={allowReactions}
          />
        </div>
      ))}

      {/* Inline reply composer — matches floating SelectionComposer style */}
      {canReply && replyOpen && (
        <div
          className="rounded-[10px] p-[var(--sp-3)]"
          style={{
            marginLeft: '36px',
            marginTop: '12px',
            background: 'var(--rd-surface-panel)',
            border: '1px solid var(--rd-border)',
          }}
        >
          <textarea
            ref={replyInputRef}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleReplySubmit();
              }
              if (e.key === 'Escape') {
                setReplyOpen(false);
                setReplyText('');
              }
            }}
            placeholder="Write a reply... (Enter to send)"
            rows={2}
            className="w-full min-h-[52px] max-h-[120px] px-[var(--sp-3)] py-[var(--sp-2)] rounded-[var(--radius-md)] text-[13px] border outline-none resize-none box-border transition-colors duration-[120ms]"
            style={{
              background: 'var(--rd-surface-input)',
              borderColor: 'var(--rd-border)',
              color: 'var(--rd-text-strong)',
              fontFamily: 'inherit',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--rd-accent)')}
            onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--rd-border)')}
          />
          <div className="flex items-center justify-between mt-[var(--sp-2)]">
            <ReplyReactionButton onToggleReaction={onToggleReaction} commentId={comment.id} />
            <div className="flex items-center gap-[var(--sp-2)]">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setReplyOpen(false);
                  setReplyText('');
                }}
                className="h-[28px] px-[var(--sp-3)] rounded-[7px] text-[13px] font-semibold cursor-pointer border-0 bg-transparent transition-colors duration-[120ms]"
                style={{ color: 'var(--rd-text-subtle)' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rd-surface-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleReplySubmit();
                }}
                disabled={!replyText.trim() || replySubmitting}
                className="h-[28px] px-[var(--sp-4)] rounded-[7px] text-[13px] font-semibold cursor-pointer border-0 transition-colors duration-[120ms] disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: 'var(--rd-accent)',
                  color: '#ffffff',
                }}
              >
                Reply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
